import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef
} from 'react'
import {
  Box,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Divider,
  SelectChangeEvent,
  InputBase,
  Tooltip
} from '@mui/material'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import MapIcon from '@mui/icons-material/Map'
import SearchIcon from '@mui/icons-material/Search'
import { useApi } from '../../../hooks/useApi'
import { getWarehouseWhitZone } from '../../Warehouse/config/api'
import { WarehouseUserAssignment } from '../../../interfaces/Warehouse/Warehouse'
import {
  getUserAdminAsignedZonesWMS,
  putUserAdminZonesWMS
} from '../config/api'
import { User } from 'oidc-react'
interface FlatZone {
  zoneId: number
  zoneName: string
  warehouseId: number
  warehouseName: string
}

export interface AssignmentRef {
  save: () => void
}

interface AssignmentUser {
  userId: number
  userAlias?: string
  email?: string
  zones?: { zoneId: number }[]
}

interface AssignmentProps {
  user?: AssignmentUser
  onLoadingChange?: (loading: boolean) => void
  onSaveSuccess?: () => void
}

const HEADER_COLORS = {
  available: '#3b6fd4',
  assigned: '#2e8b57'
}

const Assignment = forwardRef<AssignmentRef, AssignmentProps>(
  ({ user, onLoadingChange, onSaveSuccess }, ref) => {
    const { state, callApi } = useApi<WarehouseUserAssignment[]>() // obtiene solo bodegas con sus zonas
    const { state: stateZonesAsigned, callApi: callApiZonesAsigned } = useApi() // obtiene zonas que fueron asignadas al usuario
    const [allZones, setAllZones] = useState<FlatZone[]>([])
    const [assignedIds, setAssignedIds] = useState<Set<number>>(new Set())
    const [selectedAvailable, setSelectedAvailable] = useState<Set<number>>(
      new Set()
    )
    const [selectedAssigned, setSelectedAssigned] = useState<Set<number>>(
      new Set()
    )
    const [warehouseFilter, setWarehouseFilter] = useState<number>(-1)
    const [searchAvailable, setSearchAvailable] = useState('')
    const [searchAssigned, setSearchAssigned] = useState('')

    useImperativeHandle(ref, () => ({
      save: () => {
        const assignedZones = allZones.filter((z) => assignedIds.has(z.zoneId))
        const dto = {
          userId: user?.userId ?? 0,
          email: user?.email ?? '',
          assignedZones
        }
        onLoadingChange?.(true)
        putUserAdminZonesWMS(user?.userId ?? 0, dto)
          .then(() => onSaveSuccess?.())
          .catch(console.error)
          .finally(() => onLoadingChange?.(false))
      }
    }))

    useEffect(() => {
      callApi(getWarehouseWhitZone()).catch(console.error)
      callApiZonesAsigned(getUserAdminAsignedZonesWMS(user?.userId || 0)).catch(
        console.error
      )
    }, [])

    useEffect(() => {
      if (!state.data) return
      const flat: FlatZone[] = state.data.flatMap((wh) =>
        wh.zones.map((z) => ({
          zoneId: z.zoneId,
          zoneName: z.zoneName,
          warehouseId: wh.warehouseId,
          warehouseName: wh.warehouseName
        }))
      )
      setAllZones(flat)
    }, [state.data])

    useEffect(() => {
      if (!stateZonesAsigned.data) return
      const assigned = stateZonesAsigned.data as FlatZone[]
      setAssignedIds(new Set(assigned.map((z) => z.zoneId)))
    }, [stateZonesAsigned.data])

    const warehouses = state.data ?? []

    const filterByWarehouse = (zones: FlatZone[]) =>
      warehouseFilter === -1
        ? zones
        : zones.filter((z) => z.warehouseId === warehouseFilter)

    const applySearch = (zones: FlatZone[], search: string) =>
      search.trim() === ''
        ? zones
        : zones.filter((z) =>
            z.zoneName.toLowerCase().includes(search.toLowerCase())
          )

    const availableZones = applySearch(
      filterByWarehouse(allZones.filter((z) => !assignedIds.has(z.zoneId))),
      searchAvailable
    )
    const assignedZones = applySearch(
      allZones.filter((z) => assignedIds.has(z.zoneId)),
      searchAssigned
    )

    const groupByWarehouse = (zones: FlatZone[]) => {
      const map = new Map<string, FlatZone[]>()
      zones.forEach((z) => {
        if (!map.has(z.warehouseName)) map.set(z.warehouseName, [])
        map.get(z.warehouseName)!.push(z)
      })
      return map
    }

    const toggleSelect = (
      id: number,
      set: Set<number>,
      setter: React.Dispatch<React.SetStateAction<Set<number>>>
    ) => {
      const next = new Set(set)
      next.has(id) ? next.delete(id) : next.add(id)
      setter(next)
    }

    const moveToAssigned = () => {
      setAssignedIds((prev) => new Set([...prev, ...selectedAvailable]))
      setSelectedAvailable(new Set())
    }

    const moveToAvailable = () => {
      setAssignedIds((prev) => {
        const next = new Set(prev)
        selectedAssigned.forEach((id) => next.delete(id))
        return next
      })
      setSelectedAssigned(new Set())
    }

    const totalAssigned = allZones.filter((z) =>
      assignedIds.has(z.zoneId)
    ).length

    const renderPanel = (
      zones: FlatZone[],
      selected: Set<number>,
      onToggle: (id: number) => void,
      label: string,
      count: number,
      headerColor: string,
      search: string,
      onSearch: (v: string) => void,
      emptyText: string
    ) => (
      <Paper
        elevation={2}
        sx={{
          flex: 1,
          height: 380,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: headerColor
          }}
        >
          <Box display='flex' alignItems='center' gap={1}>
            <MapIcon sx={{ color: '#fff', fontSize: 18 }} />
            <Typography variant='body2' fontWeight={700} color='#fff'>
              {label}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.25)',
              borderRadius: '50%',
              minWidth: 26,
              height: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 0.8
            }}
          >
            <Typography variant='caption' fontWeight={700} color='#fff'>
              {count}
            </Typography>
          </Box>
        </Box>

        {/* Search */}
        <Box
          sx={{
            px: 1.5,
            py: 0.8,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <SearchIcon fontSize='small' color='disabled' />
          <InputBase
            placeholder='Buscar...'
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            fullWidth
            sx={{ fontSize: 13 }}
          />
        </Box>

        {/* List */}
        <Box sx={{ overflowY: 'auto', flex: 1, py: 0.5 }}>
          {Array.from(groupByWarehouse(zones).entries()).map(
            ([whName, wzones]) => (
              <Box key={whName}>
                <Typography
                  variant='body2'
                  fontWeight={700}
                  sx={{ px: 2, py: 0.5, display: 'block' }}
                >
                  {whName}
                </Typography>
                {wzones.map((z) => (
                  <Box
                    key={z.zoneId}
                    onClick={() => onToggle(z.zoneId)}
                    pl={3}
                    sx={{
                      px: 2,
                      py: 0.8,
                      cursor: 'pointer',
                      bgcolor: selected.has(z.zoneId)
                        ? `${headerColor}18`
                        : 'transparent',
                      borderLeft: selected.has(z.zoneId)
                        ? `3px solid ${headerColor}`
                        : '3px solid transparent',
                      '&:hover': { bgcolor: `${headerColor}12` },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Typography
                      variant='body2'
                      color='text.primary'
                      display='flex'
                      alignItems='center'
                      gap={0.8}
                    >
                      <Box
                        component='span'
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          bgcolor: 'text.secondary',
                          flexShrink: 0,
                          display: 'inline-block'
                        }}
                      />
                      {z.zoneName}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )
          )}

          {zones.length === 0 && (
            <Typography
              variant='body2'
              color='text.disabled'
              textAlign='center'
              mt={4}
            >
              {emptyText}
            </Typography>
          )}
        </Box>
      </Paper>
    )

    return (
      <Box display='flex' flexDirection='column' gap={2}>
        <Box>
          <Box display='flex' alignItems='center' gap={1}>
            <LocationOnIcon color='primary' />
            <Typography variant='h6' fontWeight={600}>
              Gestionar zonas — {user?.userAlias ?? ''}
            </Typography>
          </Box>
          <Typography variant='body2' color='text.secondary'>
            {totalAssigned} zonas asignadas de {allZones.length} disponibles
          </Typography>
        </Box>

        <Paper
          variant='outlined'
          sx={{
            px: 2,
            py: 1.2,
            bgcolor: 'grey.50',
            borderLeft: '4px solid #1976d2',
            borderRadius: 2
          }}
        >
          <Typography
            variant='overline'
            style={{
              fontWeight: 700,
              letterSpacing: 1.2,
              color: '#1976d2',
              display: 'block',
              marginBottom: 4
            }}
          >
            Seleccione bodega
          </Typography>
          <Box display='flex' alignItems='center' gap={1}>
            <WarehouseIcon color='action' fontSize='small' />
            <Select
              value={warehouseFilter}
              onChange={(e: SelectChangeEvent<number>) =>
                setWarehouseFilter(Number(e.target.value))
              }
              size='small'
              fullWidth
            >
              <MenuItem value={-1}>Todas las bodegas</MenuItem>
              {warehouses.map((wh) => (
                <MenuItem key={wh.warehouseId} value={wh.warehouseId}>
                  {wh.warehouseName}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Paper>

        <Paper
          variant='outlined'
          sx={{
            p: 1.5,
            bgcolor: 'grey.50',
            borderLeft: '4px solid #1976d2',
            borderRadius: 2
          }}
        >
          <Typography
            variant='overline'
            style={{
              fontWeight: 700,
              letterSpacing: 1.2,
              color: '#1976d2',
              display: 'block',
              marginBottom: 4
            }}
          >
            Asignar Zonas
          </Typography>
          <Box display='flex' gap={1} alignItems='center'>
            {renderPanel(
              availableZones,
              selectedAvailable,
              (id) => toggleSelect(id, selectedAvailable, setSelectedAvailable),
              'Zonas Disponibles',
              availableZones.length,
              HEADER_COLORS.available,
              searchAvailable,
              setSearchAvailable,
              'Sin zonas disponibles'
            )}

            <Box
              display='flex'
              flexDirection='column'
              gap={1}
              alignItems='center'
              justifyContent='center'
              height='100%'
            >
              <Tooltip title='Asignar seleccionadas' placement='right'>
                <span>
                  <IconButton
                    size='small'
                    onClick={moveToAssigned}
                    disabled={selectedAvailable.size === 0}
                    style={{
                      backgroundColor:
                        selectedAvailable.size > 0 ? '#1976d2' : '#e0e0e0',
                      color: selectedAvailable.size > 0 ? '#fff' : '#aaa',
                      borderRadius: '50%',
                      padding: 8
                    }}
                  >
                    <ChevronRightIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Quitar seleccionadas' placement='right'>
                <span>
                  <IconButton
                    size='small'
                    onClick={moveToAvailable}
                    disabled={selectedAssigned.size === 0}
                    style={{
                      backgroundColor:
                        selectedAssigned.size > 0 ? '#e53935' : '#e0e0e0',
                      color: selectedAssigned.size > 0 ? '#fff' : '#aaa',
                      borderRadius: '50%',
                      padding: 8
                    }}
                  >
                    <ChevronLeftIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {renderPanel(
              assignedZones,
              selectedAssigned,
              (id) => toggleSelect(id, selectedAssigned, setSelectedAssigned),
              'Zonas Asignadas',
              assignedZones.length,
              HEADER_COLORS.assigned,
              searchAssigned,
              setSearchAssigned,
              'No hay zonas asignadas'
            )}
          </Box>
        </Paper>
      </Box>
    )
  }
)

Assignment.displayName = 'Assignment'

export default Assignment
